/** Dialogs, confirmations, menus, tooltips and toasts. Esc closes the topmost layer only. */
import { createContext, isValidElement, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";
import { Button } from "./ui";
export { Tip } from "./tip";

// ---------------------------------------------------------------- layer stack (Esc order)

const stack: (() => void)[] = [];
function useLayer(onClose: () => void, active = true) {
  const ref = useRef(onClose);
  ref.current = onClose;
  useEffect(() => {
    if (!active) return;
    const fn = () => ref.current();
    stack.push(fn);
    return () => {
      const i = stack.lastIndexOf(fn);
      if (i >= 0) stack.splice(i, 1);
    };
  }, [active]);
}
if (typeof window !== "undefined") {
  window.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && stack.length) {
      e.preventDefault();
      stack[stack.length - 1]();
    }
  });
}

// ---------------------------------------------------------------- focus

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
export const focusables = (el: HTMLElement) => [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((x) => x.getClientRects().length > 0);

// While a dialog is open the console behind it is inert and does not scroll. Counted, so a dialog over a dialog releases only when the last closes.
const dialogs: HTMLElement[] = [];
let held: { overflow: string; padding: string } | null = null;
function holdPage(box: HTMLElement) {
  dialogs.push(box);
  if (dialogs.length === 1) {
    document.getElementById("root")?.setAttribute("inert", "");
    const bar = window.innerWidth - document.documentElement.clientWidth;
    held = { overflow: document.body.style.overflow, padding: document.body.style.paddingRight };
    document.body.style.overflow = "hidden";
    if (bar > 0) document.body.style.paddingRight = `${bar}px`;
  }
  return () => {
    const i = dialogs.lastIndexOf(box);
    if (i >= 0) dialogs.splice(i, 1);
    if (dialogs.length || !held) return;
    document.getElementById("root")?.removeAttribute("inert");
    document.body.style.overflow = held.overflow;
    document.body.style.paddingRight = held.padding;
    held = null;
  };
}

/**
 * Keyboard for a menu: ↑ ↓ move (wrapping), Home and End jump, Enter and Space
 * act (they are buttons), Tab closes and returns to the trigger.
 */
export function menuKeys(e: React.KeyboardEvent, list: HTMLElement | null, done: () => void) {
  if (!list) return;
  if (e.key === "Tab") return (e.preventDefault(), e.stopPropagation(), done());
  const items = [...list.querySelectorAll<HTMLElement>('[role^="menuitem"]:not(:disabled)')];
  if (!items.length) return;
  const i = items.indexOf(document.activeElement as HTMLElement);
  const go = (n: number) => (e.preventDefault(), items[(n + items.length) % items.length].focus());
  if (e.key === "ArrowDown") go(i + 1);
  else if (e.key === "ArrowUp") go(i < 0 ? items.length - 1 : i - 1);
  else if (e.key === "Home" && i >= 0) go(0);
  else if (e.key === "End" && i >= 0) go(items.length - 1);
}

// ---------------------------------------------------------------- dialog

/**
 * What still blocks a form's action, said the one way every Inlinea console
 * says it: the warning glyph, then "Still needed: a reason, a name." in the
 * amber of a warning, medium weight. A dialog's `note` takes it as is; a page
 * footer renders it on its own.
 */
export function stillNeeded(what: string | string[]): ReactNode {
  const items = (Array.isArray(what) ? what : [what]).map((w) => w.trim()).filter(Boolean);
  if (!items.length) return null;
  return <NeededNote text={`Still needed: ${items.join(", ")}.`} />;
}

function NeededNote({ text }: { text: string }) {
  return (
    <span className="note needed" title={text} data-testid="still-needed">
      <Icon name="warning" size={14} />
      <span className="needed-text">{text}</span>
    </span>
  );
}

export function Dialog({
  open, onClose, title, lead, icon, iconTone, children, footer, note, size, busy,
}: {
  open: boolean; onClose: () => void; title: ReactNode; lead?: ReactNode; icon?: string; iconTone?: "red" | "green" | "amber";
  children?: ReactNode; footer?: ReactNode; note?: ReactNode; size?: "wide" | "xwide"; busy?: boolean;
}) {
  useLayer(() => !busy && onClose(), open);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = box.current;
    if (!open || !el) return;
    const prev = document.activeElement as HTMLElement | null;
    const release = holdPage(el);
    // First choice: a control marked data-autofocus, then the first field, then the dialog's default (Cancel in a Confirm), then the primary action.
    const t = setTimeout(() => (el.querySelector<HTMLElement>("[data-autofocus]") ?? el.querySelector<HTMLElement>("input:not([disabled]),textarea:not([disabled]),select:not([disabled])") ?? el.querySelector<HTMLElement>("[data-default-focus]") ?? el.querySelector<HTMLElement>("button.primary:not(:disabled)") ?? el).focus(), 30);
    const trap = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || e.defaultPrevented || dialogs[dialogs.length - 1] !== el) return;
      const f = focusables(el);
      const a = document.activeElement as HTMLElement | null;
      if (!f.length) return (e.preventDefault(), el.focus());
      const first = f[0], last = f[f.length - 1];
      if (!a || !el.contains(a) || a === el) (e.preventDefault(), (e.shiftKey ? last : first).focus());
      else if (e.shiftKey && a === first) (e.preventDefault(), last.focus());
      else if (!e.shiftKey && a === last) (e.preventDefault(), first.focus());
    };
    document.addEventListener("keydown", trap);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", trap);
      release();
      prev?.focus?.();
    };
  }, [open]);
  if (!open) return null;
  const color = iconTone === "red" ? "var(--rd)" : iconTone === "green" ? "var(--gn)" : iconTone === "amber" ? "var(--am)" : "var(--tx)";
  return createPortal(
    <div className="backdrop" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className={`dialog ${size ?? ""}`} role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : undefined} ref={box} tabIndex={-1}>
        <div className="dh">
          {icon && (
            <div className="tile">
              <Icon name={icon} size={24} style={{ color }} />
            </div>
          )}
          <div className="grow">
            <h2>{title}</h2>
            {lead && <p>{lead}</p>}
          </div>
          <Button kind="ghost" size="sm" iconOnly icon="dismiss" aria-label="Close" onClick={onClose} disabled={busy} />
        </div>
        <div className="db">{children}</div>
        {(footer || note) && (
          <div className="df">
            {note && isValidElement(note) && note.type === NeededNote
              ? note
              : note && (
                  <span className="note">
                    <Icon name="info" size={14} />
                    {note}
                  </span>
                )}
            {!note && <span style={{ marginRight: "auto" }} />}
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** A destructive or weighty action: what it is, what happens, optional type-to-confirm. */
export function Confirm({
  open, onClose, onConfirm, title, lead, happens, confirmText, action, danger = true, busy, children, icon,
}: {
  open: boolean; onClose: () => void; onConfirm: () => void; title: string; lead?: ReactNode; happens?: string[]; confirmText?: string; action: string;
  danger?: boolean; busy?: boolean; children?: ReactNode; icon?: string;
}) {
  const [typed, setTyped] = useState("");
  useEffect(() => setTyped(""), [open]);
  const ok = !confirmText || typed.trim() === confirmText;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      busy={busy}
      title={title}
      lead={lead}
      icon={icon ?? (danger ? "warning" : "question-circle")}
      iconTone={danger ? "red" : undefined}
      footer={
        <>
          <Button onClick={onClose} disabled={busy} data-default-focus>
            Cancel
          </Button>
          <Button kind={danger ? "danger" : "primary"} disabled={!ok} busy={busy} onClick={onConfirm}>
            {action}
          </Button>
        </>
      }
    >
      {children}
      {happens && happens.length > 0 && (
        <div className="whathappens">
          <div>What happens</div>
          {happens.map((h, i) => (
            <div key={i}>
              <Icon name="arrow-right" size={14} style={{ color: "var(--tx3)", marginTop: 2 }} />
              {h}
            </div>
          ))}
        </div>
      )}
      {confirmText && (
        <div className="field" style={{ marginTop: 14 }}>
          <label>
            Type <span className="mono">{confirmText}</span> to confirm.
          </label>
          <input className="input mono" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} />
        </div>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------- menu

export type MenuItem = { label: string; icon?: string; onClick?: () => void; danger?: boolean; disabled?: boolean | string; hint?: string } | "sep";

export function Menu({ items, trigger, align = "end", header, block }: { items: MenuItem[]; trigger: (open: () => void, isOpen: boolean) => ReactNode; align?: "start" | "end"; header?: ReactNode; block?: boolean }) {
  const [pos, setPos] = useState<{ x: number; y: number; up: boolean } | null>(null);
  const anchor = useRef<HTMLSpanElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const toTrigger = () => anchor.current?.querySelector<HTMLElement>("button,a[href],[tabindex]")?.focus();
  // Closing while focus is inside the menu hands focus back to the trigger, so the keyboard never lands on the page's end.
  const close = useCallback(() => {
    const inside = !!list.current?.contains(document.activeElement);
    setPos(null);
    if (inside) toTrigger();
  }, []);
  useLayer(close, !!pos);
  useEffect(() => {
    if (!pos) return;
    list.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus();
    const fn = (e: Event) => !list.current?.contains(e.target as Node) && close();
    window.addEventListener("scroll", fn, true);
    window.addEventListener("resize", fn);
    return () => {
      window.removeEventListener("scroll", fn, true);
      window.removeEventListener("resize", fn);
    };
  }, [pos, close]);
  const open = () => {
    if (pos) return close();
    const r = anchor.current!.getBoundingClientRect();
    const up = r.bottom + 300 > window.innerHeight;
    setPos({ x: align === "end" ? r.right : r.left, y: up ? r.top : r.bottom, up });
  };
  return (
    <>
      <span ref={anchor} style={block ? { display: "block", minWidth: 0 } : { display: "inline-flex" }} onClick={(e) => e.stopPropagation()}>
        {trigger(open, !!pos)}
      </span>
      {pos &&
        createPortal(
          <>
            <div className="menulayer" onMouseDown={close} />
            <div
              ref={list}
              className={`menu${pos.up ? " up" : ""}`}
              role="menu"
              onKeyDown={(e) => menuKeys(e, list.current, close)}
              style={{ minWidth: block ? anchor.current?.offsetWidth : undefined, top: pos.up ? undefined : pos.y + 4, bottom: pos.up ? window.innerHeight - pos.y + 4 : undefined, ...(align === "end" ? { right: window.innerWidth - pos.x } : { left: pos.x }) }}
            >
              {header && <div className="mhead">{header}</div>}
              {items.map((it, i) =>
                it === "sep" ? (
                  <hr key={i} />
                ) : (
                  <button
                    key={i}
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    className={it.danger ? "danger" : ""}
                    disabled={!!it.disabled}
                    title={typeof it.disabled === "string" ? it.disabled : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      close();
                      it.onClick?.();
                    }}
                  >
                    {it.icon && <Icon name={it.icon} size={15} />}
                    {it.label}
                    {it.hint && <span className="mhint">{it.hint}</span>}
                  </button>
                ),
              )}
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

export function MoreMenu({ items, label = "More actions" }: { items: MenuItem[]; label?: string }) {
  return <Menu items={items} trigger={(open, isOpen) => <Button kind="ghost" size="sm" iconOnly icon="more-horizontal" aria-label={label} aria-haspopup="menu" aria-expanded={isOpen} onClick={open} />} />;
}

// ---------------------------------------------------------------- toast

type ToastItem = { id: number; text: string; tone?: "ok" | "err"; out?: boolean };
const ToastCtx = createContext<(text: string, tone?: "ok" | "err") => void>(() => {});
export const useToast = () => useContext(ToastCtx);

/** Confirmations leave after 3.4 s; errors stay 8 s, announce at once and can be dismissed. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const dismiss = useCallback((id: number) => {
    setItems((x) => x.map((t) => (t.id === id ? { ...t, out: true } : t)));
    setTimeout(() => setItems((x) => x.filter((t) => t.id !== id)), 200);
  }, []);
  const push = useCallback((text: string, tone?: "ok" | "err") => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x.slice(-2), { id, text, tone }]);
    setTimeout(() => dismiss(id), tone === "err" ? 8000 : 3400);
  }, [dismiss]);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      {createPortal(
        <div className="toasts" aria-live="polite">
          {items.map((t) => (
            <div key={t.id} className={`toast${t.tone === "err" ? " err" : ""}${t.out ? " out" : ""}`} role={t.tone === "err" ? "alert" : "status"}>
              <Icon name={t.tone === "err" ? "error-circle" : "checkmark-circle"} size={16} style={{ color: t.tone === "err" ? "#F97066" : "#32D583" }} />
              <span className="grow">{t.text}</span>
              {t.tone === "err" && (
                <button type="button" className="tclose" aria-label="Dismiss" onClick={() => dismiss(t.id)}>
                  <Icon name="dismiss" size={14} />
                </button>
              )}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastCtx.Provider>
  );
}
